import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError, fmtDate, fmtTime } from "../lib/api";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Lock, Send, ChevronLeft, UserCheck, UserX, HelpCircle, CalendarDays, Flame as FlameIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PhotoAttach from "../components/PhotoAttach";
import { ProgressRing } from "../components/ProgressRing";
import { Confetti } from "../components/Confetti";
import { t, taskText } from "../lib/i18n";

const STATUSES = [
    { key: "DONE", label_key: "run_status_done" },
    { key: "IN_PROGRESS", label_key: "run_status_in_progress" },
    { key: "ESCALATED", label_key: "run_status_escalated" },
    { key: "NA", label_key: "run_status_na" },
];

const STATUS_BG = {
    DONE: "bg-emerald-500 text-white",
    IN_PROGRESS: "bg-amber-500 text-white",
    ESCALATED: "bg-[#E02B2B] text-white",
    NA: "bg-gray-500 text-white",
    PENDING: "",
};

export default function ChecklistRunPage() {
    const { siteId, runId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openSections, setOpenSections] = useState({});
    const [escalatingTask, setEscalatingTask] = useState(null);
    const [escalationText, setEscalationText] = useState("");
    const [escalationAssetId, setEscalationAssetId] = useState("");
    const [assets, setAssets] = useState([]);
    const [signOffOpen, setSignOffOpen] = useState(false);
    const [signOffReason, setSignOffReason] = useState("");
    const [signOffResult, setSignOffResult] = useState(null); // {summary, whatsapp_link}
    const [confettiOn, setConfettiOn] = useState(false);
    const [indicators, setIndicators] = useState({});
    const [compliance, setCompliance] = useState({});
    const [site, setSite] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const load = useCallback(async () => {
        try {
            const url = runId ? `/runs/${runId}` : `/sites/${siteId}/runs/today`;
            const { data } = await api.get(url);
            setRun(data);
            const initial = {};
            for (const t of data.tasks) initial[t.section_code] = true;
            setOpenSections((prev) => (Object.keys(prev).length ? prev : initial));
            if (data.locked && data.signoff_summary) {
                setSignOffResult({
                    summary: data.signoff_summary,
                    whatsapp_link: data.signoff_whatsapp_link,
                    sign_off: data.sign_off,
                    persisted: true,
                });
            } else {
                setSignOffResult(null);
            }
            try {
                const ind = await api.get(`/sites/${data.site_id}/attendance-indicators`, { params: { date: data.run_date } });
                setIndicators(ind.data || {});
            } catch {}
            try {
                const cm = await api.get(`/sites/${data.site_id}/compliance/task-mapping`);
                setCompliance(cm.data || {});
            } catch {}
            // Load equipment asset options for escalation picker
            try {
                const s = await api.get(`/sites/${data.site_id}`);
                setSite(s.data);
            } catch {}
            try {
                const c = await api.get(`/sites/${data.site_id}/consumables`);
                const eqs = (c.data || []).filter((r) => r.enabled && r.item?.category === "EQUIPMENT");
                setAssets(eqs);
            } catch {}
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    }, [siteId, runId]);

    useEffect(() => {
        load();
    }, [load]);

    const sections = useMemo(() => {
        if (!run) return [];
        const grouped = {};
        for (const t of run.tasks) {
            if (!grouped[t.section_code]) grouped[t.section_code] = { code: t.section_code, title: t.section_title, tasks: [] };
            grouped[t.section_code].tasks.push(t);
        }
        return Object.values(grouped);
    }, [run]);

    const progress = useMemo(() => {
        if (!run) return { done: 0, total: 0, critDone: 0, critTotal: 0, esc: 0 };
        const total = run.tasks.length;
        const done = run.tasks.filter((t) => t.status === "DONE").length;
        const critTotal = run.tasks.filter((t) => t.priority === "CRITICAL").length;
        const critDone = run.tasks.filter((t) => t.priority === "CRITICAL" && t.status === "DONE").length;
        const esc = run.tasks.filter((t) => t.status === "ESCALATED").length;
        return { done, total, critDone, critTotal, esc };
    }, [run]);

    const locked = !!run?.locked;

    async function setStatus(taskNumber, status, escalation_note = null) {
        if (locked) return;
        const key = `${taskNumber}-${status}`;
        setSavingKey(key);
        // optimistic
        setRun((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                tasks: prev.tasks.map((t) =>
                    t.number === taskNumber
                        ? {
                              ...t,
                              status,
                              done_at: status === "DONE" ? new Date().toISOString() : null,
                              escalation_note: status === "ESCALATED" ? escalation_note : null,
                          }
                        : t
                ),
            };
        });
        try {
            const { data } = await api.patch(`/runs/${run.id}/task/${taskNumber}`, { status, escalation_note });
            setRun((prev) => ({
                ...prev,
                tasks: prev.tasks.map((t) => (t.number === taskNumber ? data.task : t)),
            }));
        } catch (e) {
            // If offline / network error, queue the action so it retries when back online.
            const netErr = !navigator.onLine || (e && e.message && /network|failed|timeout/i.test(String(e.message))) || (!e.response);
            if (netErr) {
                try {
                    const { queueTaskStatus } = await import("../lib/offlineQueue");
                    await queueTaskStatus(run.id, taskNumber, status, escalation_note);
                    toast("Queued — will sync when back online", { icon: "🕒" });
                } catch { toast.error(apiError(e)); }
            } else {
                toast.error(apiError(e));
                await load();
            }
        } finally {
            setSavingKey(null);
        }
    }

    function onTapStatus(task, statusKey) {
        if (locked) return;
        if (statusKey === "ESCALATED") {
            setEscalatingTask(task);
            setEscalationText(task.escalation_note || "");
        } else {
            setStatus(task.number, statusKey);
        }
    }

    async function submitEscalation() {
        if (!escalationText.trim()) {
            toast.error("A note is required for escalations");
            return;
        }
        const payload = { status: "ESCALATED", escalation_note: escalationText.trim() };
        if (escalationAssetId) payload.asset_item_id = escalationAssetId;
        try {
            setSavingKey(`${escalatingTask.number}-ESCALATED`);
            const { data } = await api.patch(`/runs/${run.id}/task/${escalatingTask.number}`, payload);
            setRun((prev) => ({ ...prev, tasks: prev.tasks.map((t) => (t.number === escalatingTask.number ? data.task : t)) }));
            setEscalatingTask(null);
            setEscalationText("");
            setEscalationAssetId("");
        } catch (e) {
            toast.error(apiError(e));
        } finally { setSavingKey(null); }
    }

    async function submitSignOff() {
        try {
            const { data } = await api.post(`/runs/${run.id}/signoff`, {
                incomplete_reason: signOffReason.trim() || null,
            });
            toast.success("Run signed off and locked");
            setSignOffResult({ summary: data.summary, whatsapp_link: data.whatsapp_link, sign_off: data.sign_off });
            setSignOffOpen(false);
            // Celebrate when the run signed off with 100% CRITICAL completion (no unsigned CRITICAL tasks).
            if (progress.critTotal > 0 && progress.critDone >= progress.critTotal) {
                setConfettiOn(true);
            }
            await load();
        } catch (e) {
            toast.error(apiError(e));
        }
    }

    if (loading) {
        return (
            <div className="mx-auto max-w-3xl p-6" data-testid="run-loading">
                <div className="h-32 rounded-xl bg-white animate-pulse" />
            </div>
        );
    }

    if (!run) return null;

    const critIncomplete = run.tasks.filter((t) => t.priority === "CRITICAL" && !["DONE", "NA"].includes(t.status));
    const pct = progress.total ? (progress.done / progress.total) * 100 : 0;

    return (
        <div className="pb-32">
            <Confetti trigger={confettiOn} onDone={() => setConfettiOn(false)} />
            {/* Theme banner */}
            <div className="bg-gradient-to-r from-[#1E3355] to-[#2B4773] text-white">
                <div className="mx-auto max-w-3xl px-4 py-4 sm:py-5">
                    <button onClick={() => nav("/")} className="text-white/70 hover:text-white text-xs inline-flex items-center gap-1 mb-2" data-testid="back-to-sites">
                        <ChevronLeft className="w-3 h-3" /> All sites
                    </button>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-white/60">{fmtDate(run.run_date)} · {run.site_name}</div>
                            <div className="font-display font-bold text-lg sm:text-xl mt-1" data-testid="theme-banner">
                                Day {run.cycle_day} — {run.theme}
                            </div>
                            <div className="text-xs text-white/70 mt-0.5">Manager: {run.manager_name}</div>
                        </div>
                        {run.template_is_draft && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-semibold bg-amber-400/20 text-amber-200 border border-amber-300/40 uppercase tracking-wider" data-testid="run-draft-badge">
                                Draft template
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Sticky progress */}
            <div className="sticky top-14 z-30 bg-white border-b border-gray-200 fx-shadow-sticky" data-testid="progress-bar">
                <div className="mx-auto max-w-3xl px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <ProgressRing
                                value={pct}
                                size={44}
                                strokeWidth={5}
                                accent="#1E3355"
                                label={`${Math.round(pct)}%`}
                                testid="progress-ring"
                            />
                            <div className="flex flex-col text-xs sm:text-sm gap-0.5">
                                <span className="text-[#1E3355] font-semibold" data-testid="progress-done">
                                    {progress.done}/{progress.total} done
                                </span>
                                <span className="text-gray-500">
                                    Critical <span className="font-semibold text-[#1E3355]" data-testid="progress-critical">{progress.critDone}/{progress.critTotal}</span>
                                    <span className={progress.esc > 0 ? "text-[#E02B2B] font-semibold inline-flex items-center gap-1 ml-3" : "text-gray-400 inline-flex items-center gap-1 ml-3"} data-testid="progress-esc">
                                        <AlertTriangle className="w-3.5 h-3.5" /> {progress.esc}
                                    </span>
                                </span>
                            </div>
                        </div>
                        {locked ? (
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="locked-badge">
                                    <Lock className="w-3.5 h-3.5" /> Locked
                                </span>
                                {run.signoff_summary && (
                                    <button
                                        onClick={() => setSignOffResult({ summary: run.signoff_summary, whatsapp_link: run.signoff_whatsapp_link, sign_off: run.sign_off, persisted: true })}
                                        className="fx-tap inline-flex items-center gap-1 text-xs font-semibold text-[#1E3355] bg-[#F4F6F9] hover:bg-gray-200 px-2.5 py-1 rounded-md"
                                        data-testid="view-summary-btn"
                                    >
                                        View summary
                                    </button>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={() => setSignOffOpen(true)}
                                className="fx-tap inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
                                data-testid="open-signoff-btn"
                            >
                                <CheckCircle2 className="w-4 h-4" /> Sign off
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Sections */}
            <div className="mx-auto max-w-3xl px-4 py-5 space-y-4">
                {sections.map((section) => {
                    const open = openSections[section.code] ?? true;
                    const sectionDone = section.tasks.filter((t) => t.status === "DONE").length;
                    return (
                        <div key={section.code} className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden" data-testid={`section-${section.code}`}>
                            <button
                                className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 hover:bg-gray-50"
                                onClick={() => setOpenSections((s) => ({ ...s, [section.code]: !open }))}
                                data-testid={`section-toggle-${section.code}`}
                            >
                                <div className="text-left">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Section {section.code}</div>
                                    <div className="font-display font-semibold text-[#1E3355] text-sm sm:text-base">{section.title}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-xs text-gray-500 font-medium">{sectionDone}/{section.tasks.length}</div>
                                    {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </div>
                            </button>
                            {section.code === "F" && (
                                <div className="px-4 sm:px-5 pb-3 -mt-1">
                                    <a
                                        href={`/sites/${run.site_id}/consumables`}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E3355] bg-[#F4F6F9] hover:bg-gray-200 rounded-md px-2.5 py-1.5"
                                        data-testid="section-F-update-stock"
                                    >
                                        Update stock counts →
                                    </a>
                                </div>
                            )}
                            {open && (
                                <div className="divide-y divide-gray-100">
                                    {section.tasks.map((t) => (
                                        <TaskRow
                                            key={t.number}
                                            task={t}
                                            locked={locked}
                                            onTapStatus={onTapStatus}
                                            savingKey={savingKey}
                                            site={site}
                                            siteId={run.site_id}
                                            runId={run.id}
                                            indicator={indicators[String(t.number)]}
                                            compliance={compliance[String(t.number)]}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Escalation bottom sheet */}
            {escalatingTask && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-slide-up" data-testid="escalation-sheet">
                    <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
                        <div className="p-5">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Escalate</div>
                            <div className="font-display font-semibold text-[#1E3355] mt-1">Task #{escalatingTask.number}</div>
                            <div className="text-sm text-gray-600 mt-1">{escalatingTask.text}</div>
                            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Escalation note <span className="text-[#E02B2B]">*</span></label>
                            <textarea
                                value={escalationText}
                                onChange={(e) => setEscalationText(e.target.value)}
                                rows={4}
                                autoFocus
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]"
                                placeholder="Describe the issue and any actions taken…"
                                data-testid="escalation-note-input"
                            />
                            {assets.length > 0 && (
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Related asset <span className="text-gray-400">(optional)</span></label>
                                    <select
                                        value={escalationAssetId}
                                        onChange={(e) => setEscalationAssetId(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white"
                                        data-testid="escalation-asset-select"
                                    >
                                        <option value="">— None —</option>
                                        {assets.map((a) => (
                                            <option key={a.item.id} value={a.item.id}>
                                                {a.item.name}{a.asset_fix_time_hours ? ` · SLA ${a.asset_fix_time_hours}h` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Photo evidence</label>
                                <div className="text-[11px] text-gray-500 mb-1.5">Attach a photo of the issue — recommended for every escalation.</div>
                                <PhotoAttach
                                    parentType="run_task"
                                    parentId={`${run.id}:${escalatingTask.number}`}
                                    siteId={run.site_id}
                                    tag="escalation"
                                    testIdPrefix={`escalation-${escalatingTask.number}-photo`}
                                />
                            </div>
                        </div>
                        <div className="flex border-t border-gray-100">
                            <button
                                onClick={() => { setEscalatingTask(null); setEscalationText(""); }}
                                className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
                                data-testid="escalation-cancel"
                            >Cancel</button>
                            <button
                                onClick={submitEscalation}
                                className="flex-1 py-3 text-sm font-semibold text-white bg-[#E02B2B] hover:bg-[#C92525]"
                                data-testid="escalation-submit"
                            >Save escalation</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sign-off modal */}
            {signOffOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="signoff-modal">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="p-5">
                            <div className="font-display font-bold text-lg text-[#1E3355]">Sign off today's run</div>
                            <div className="text-sm text-gray-600 mt-1">
                                Once signed off the run is locked and cannot be edited.
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                                <div className="bg-[#F4F6F9] rounded-md py-2">
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Done</div>
                                    <div className="font-display font-bold text-[#1E3355]">{progress.done}/{progress.total}</div>
                                </div>
                                <div className="bg-[#F4F6F9] rounded-md py-2">
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Critical</div>
                                    <div className="font-display font-bold text-[#1E3355]">{progress.critDone}/{progress.critTotal}</div>
                                </div>
                                <div className="bg-[#F4F6F9] rounded-md py-2">
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Escalations</div>
                                    <div className="font-display font-bold text-[#E02B2B]">{progress.esc}</div>
                                </div>
                            </div>
                            {critIncomplete.length > 0 && (
                                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="signoff-warning">
                                    <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {critIncomplete.length} CRITICAL task{critIncomplete.length > 1 ? "s" : ""} not Done/N/A</div>
                                    <ul className="mt-1 list-disc pl-5 text-xs space-y-0.5">
                                        {critIncomplete.slice(0, 5).map((t) => <li key={t.number}>#{t.number} {t.text}</li>)}
                                        {critIncomplete.length > 5 && <li>… and {critIncomplete.length - 5} more</li>}
                                    </ul>
                                    <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Reason (required)</label>
                                    <textarea
                                        value={signOffReason}
                                        onChange={(e) => setSignOffReason(e.target.value)}
                                        rows={3}
                                        className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]"
                                        placeholder="Explain why the critical tasks are incomplete…"
                                        data-testid="signoff-reason-input"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex border-t border-gray-100">
                            <button
                                onClick={() => setSignOffOpen(false)}
                                className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
                                data-testid="signoff-cancel"
                            >Cancel</button>
                            <button
                                onClick={submitSignOff}
                                className="flex-1 py-3 text-sm font-semibold text-white bg-[#1E3355] hover:bg-[#15243C]"
                                data-testid="signoff-confirm"
                            >Confirm sign-off</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Post-signoff summary */}
            {signOffResult && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="signoff-result">
                    <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="p-5">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
                                <div>
                                    <div className="font-display font-bold text-lg text-[#1E3355]">Signed off · {fmtTime(signOffResult.sign_off.signed_at)}</div>
                                    <div className="text-xs text-gray-500">Summary generated. Share via WhatsApp or check email outbox.</div>
                                </div>
                            </div>
                            <pre className="mt-4 whitespace-pre-wrap text-xs bg-[#F4F6F9] border border-gray-100 rounded-md p-3 max-h-64 overflow-auto text-gray-700" data-testid="signoff-summary-text">
                                {signOffResult.summary}
                            </pre>
                        </div>
                        <div className="flex border-t border-gray-100">
                            <button
                                onClick={() => setSignOffResult(null)}
                                className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
                                data-testid="signoff-result-close"
                            >Close</button>
                            <a
                                href={signOffResult.whatsapp_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 py-3 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center justify-center gap-1.5"
                                data-testid="whatsapp-share-link"
                            >
                                <Send className="w-4 h-4" /> WhatsApp share
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TaskRow({ task, locked, onTapStatus, savingKey, site, siteId, runId, indicator, compliance }) {
    const { user: currentUser } = useAuth();
    const lang = currentUser?.language || "en";
    const isCritical = task.priority === "CRITICAL";
    return (
        <div className="p-4 sm:p-5" data-testid={`task-row-${task.number}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold text-gray-400 tabular-nums">#{task.number}</span>
                        {isCritical ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-[#E02B2B]" data-testid={`priority-${task.number}`}>{t(currentUser, "run_priority_critical")}</span>
                        ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200" data-testid={`priority-${task.number}`}>{t(currentUser, "run_priority_standard")}</span>
                        )}
                        {task.status === "DONE" && task.done_at && (
                            <span className="text-[10px] text-emerald-700 font-medium">✓ {fmtTime(task.done_at)}</span>
                        )}
                        {indicator && <AttendanceIndicator indicator={indicator} taskNumber={task.number} />}
                    </div>
                    <div className="mt-1.5 text-sm text-gray-800 leading-snug" data-testid={`task-text-${task.number}`}>{taskText(task, lang)}</div>
                    {task.reference_note && (
                        <div className="mt-1 text-[11px] text-gray-500 italic">{t(currentUser, "note")}: {task.reference_note}</div>
                    )}
                    {site?.client_contact_name && (task.number === 27 || task.number === 32) && (
                        <div className="mt-1 text-[11px] text-[#1E3355] font-medium" data-testid={`client-contact-hint-${task.number}`}>
                            Contact: {site.client_contact_name}
                            {site.client_contact_phone ? ` · ${site.client_contact_phone}` : ""}
                        </div>
                    )}
                    {compliance && <ComplianceLine compliance={compliance} taskNumber={task.number} />}
                    {task.escalation_note && task.status === "ESCALATED" && (
                        <div className="mt-2 rounded-md bg-red-50 border border-red-100 px-2.5 py-1.5 text-[11px] text-[#7f1d1d]" data-testid={`escalation-note-${task.number}`}>
                            <span className="font-semibold">Escalation:</span> {task.escalation_note}
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-3 flex w-full overflow-hidden rounded-md border border-gray-200 divide-x divide-gray-200">
                {STATUSES.map((s) => {
                    const active = task.status === s.key;
                    return (
                        <button
                            key={s.key}
                            disabled={locked || savingKey === `${task.number}-${s.key}`}
                            onClick={() => onTapStatus(task, s.key)}
                            className={`flex-1 text-center text-[11px] sm:text-xs font-semibold py-2.5 min-h-[44px] transition-colors ${active ? STATUS_BG[s.key] : "bg-gray-50 text-gray-500 hover:bg-gray-100 disabled:opacity-50"}`}
                            data-testid={`task-${task.number}-status-${s.key}`}
                        >
                            {t(currentUser, `run_status_${s.key.toLowerCase()}`)}
                        </button>
                    );
                })}
            </div>
            <PhotoAttach
                parentType="run_task"
                parentId={`${runId}:${task.number}`}
                siteId={siteId}
                disabled={locked}
                compact
                testIdPrefix={`task-${task.number}-photo`}
            />
        </div>
    );
}

function ComplianceLine({ compliance, taskNumber }) {
    if (taskNumber === 53) {
        const items = compliance.items || [];
        if (items.length === 0) return null; // task 53 hidden when nothing due
        return (
            <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-800" data-testid={`compliance-line-${taskNumber}`}>
                <div className="font-semibold flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {items.length} item{items.length > 1 ? "s" : ""} due soon / overdue:</div>
                <ul className="mt-1 list-disc list-inside">
                    {items.map((it) => (
                        <li key={it.id}>
                            {it.is_fire && <FlameIcon className="inline w-3 h-3 text-[#E02B2B] mr-0.5" />}{it.name}{it.vendor ? ` · ${it.vendor}` : ""} — <span className={it.status === "OVERDUE" ? "text-[#E02B2B] font-semibold" : "text-amber-800 font-semibold"}>{it.status === "OVERDUE" ? `${-it.days_to_due}d overdue` : `due in ${it.days_to_due}d`}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
    if (!compliance.status) return null;
    const chipCls = compliance.status === "OVERDUE" ? "bg-red-50 border-red-200 text-[#E02B2B]" : compliance.status === "DUE_SOON" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800";
    return (
        <div className={`mt-2 rounded-md border px-2.5 py-1.5 text-[11px] ${chipCls}`} data-testid={`compliance-line-${taskNumber}`}>
            {compliance.is_fire && <FlameIcon className="inline w-3 h-3 mr-0.5" />}
            <span className="font-semibold">{compliance.name}:</span> last done {compliance.last_done_date ? fmtDate(compliance.last_done_date) : "never"} · next due {fmtDate(compliance.next_due_date)}
            <span className="ml-1 font-semibold">· {compliance.status === "OVERDUE" ? `OVERDUE (${-compliance.days_to_due}d)` : compliance.status === "DUE_SOON" ? `DUE SOON (${compliance.days_to_due}d)` : "OK"}</span>
        </div>
    );
}

function AttendanceIndicator({ indicator, taskNumber }) {
    if (!indicator) return null;
    if (indicator.state === "PRESENT") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`attendance-indicator-${taskNumber}`}>
                <UserCheck className="w-3 h-3" /> {indicator.label} present {indicator.check_in ? `· ${fmtTime(indicator.check_in)}` : ""}
            </span>
        );
    }
    if (indicator.state === "ABSENT") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-[#E02B2B] border border-red-200" data-testid={`attendance-indicator-${taskNumber}`}>
                <UserX className="w-3 h-3" /> {indicator.label} ABSENT{indicator.cover_arranged ? " · cover" : ""}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200" data-testid={`attendance-indicator-${taskNumber}`}>
            <HelpCircle className="w-3 h-3" /> {indicator.label} — unmarked
        </span>
    );
}
